import { Link } from 'react-router-dom'

import { styled } from 'styled-system/jsx'

const Card = styled('article', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    boxShadow: '0 12px 32px -16px rgba(128, 84, 255, 0.15)',
    border: '1.5px solid transparent',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    transition:
      'transform {durations.fast} {easings.appleStandard}, border-color {durations.fast} {easings.appleStandard}',
    _hover: {
      transform: 'translateY(-2px)',
      borderColor: 'quickpass.primary',
    },
  },
})

const Hero = styled('div', {
  base: {
    height: '160px',
    backgroundColor: 'quickpass.surfaceCool',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
  },
})

const HeroPlaceholder = styled('div', {
  base: {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '36px',
  },
})

const SiteBadge = styled('span', {
  base: {
    position: 'absolute',
    top: '10px',
    left: '10px',
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    paddingX: '10px',
    paddingY: '4px',
    borderRadius: 'pill',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    color: 'quickpass.ink',
    backdropFilter: 'blur(2px)',
  },
})

const ScoreCorner = styled('div', {
  base: {
    position: 'absolute',
    top: '10px',
    right: '10px',
  },
})

const CompareToggle = styled('button', {
  base: {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    width: '30px',
    height: '30px',
    borderRadius: 'sm',
    border: '1.5px solid',
    borderColor: '#FFFFFF',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    color: 'quickpass.inkSoft',
    fontSize: '14px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(2px)',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}, transform {durations.fast} {easings.appleStandard}',
    _hover: {
      transform: 'scale(1.05)',
    },
  },
  variants: {
    isSelected: {
      true: {
        backgroundColor: 'quickpass.primary',
        color: '#FFFFFF',
        borderColor: 'quickpass.primary',
      },
    },
  },
})

const Body = styled('div', {
  base: {
    padding: '16px 18px 18px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
})

const Title = styled(Link, {
  base: {
    fontFamily: 'display',
    fontSize: '17px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineClamp: '2',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color {durations.fast} {easings.appleStandard}',
    _hover: {
      color: 'quickpass.primary',
    },
  },
})

const Price = styled('div', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '800',
    color: 'quickpass.primary',
    letterSpacing: '-0.3px',
  },
})

const MetaRow = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
})

const MetaDivider = styled('span', {
  base: {
    color: '#D8D2EC',
  },
})

const Footer = styled('footer', {
  base: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    paddingTop: '8px',
    borderTop: '1px solid',
    borderColor: '#F0EDFA',
  },
})

const FooterStamp = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    color: 'quickpass.inkSoft',
    marginTop: '4px',
  },
})

const FooterActions = styled('div', {
  base: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
})

const IconButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    transition: 'color {durations.fast} {easings.appleStandard}',
    _hover: { color: 'quickpass.primary' },
    _disabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
    },
  },
  variants: {
    tone: {
      neutral: {},
      danger: {
        _hover: { color: 'quickpass.warmth' },
      },
    },
  },
  defaultVariants: {
    tone: 'neutral',
  },
})

const ScrapeWarning = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.warmth',
    fontWeight: '600',
  },
})

const VisitLink = styled('a', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.primary',
    textDecoration: 'none',
    _hover: {
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
    },
  },
})

export {
  Card,
  Hero,
  HeroPlaceholder,
  SiteBadge,
  ScoreCorner,
  CompareToggle,
  Body,
  Title,
  Price,
  MetaRow,
  MetaDivider,
  Footer,
  FooterStamp,
  FooterActions,
  IconButton,
  ScrapeWarning,
  VisitLink,
}
